import type {ReactNode} from 'react';
import {useEffect} from 'react';
import {Badge, Box, Drawer, UnstyledButton} from '@mantine/core';
import {useDisclosure, useElementSize, useMediaQuery} from '@mantine/hooks';
import {IconListTree} from '@tabler/icons-react';
import classes from './BrowserLayout.module.css';

export interface BrowserLayoutProps {
  pane: ReactNode;
  children: ReactNode;
  /** Drawer title and the base of the toggle `aria-label`s. */
  paneTitle: string;
  /** Chip text, e.g. the active selection and its count. */
  selectionLabel: string;
  chipIcon?: ReactNode;
  /** Closes the narrow-viewport Drawer whenever it changes. */
  selectionKey?: string;
  paneWidth?: number;
  minContentWidth?: number;
  classNames?: {root?: string; pane?: string; content?: string};
}

/**
 * Two-pane browser shell; the wide pane sticks to the viewport and caps at its height.
 * Narrow viewports or containers use a Drawer opened by the active-selection chip.
 * @registry Responsive two-pane browser shell (viewport-capped sticky pane + content) with a mobile drawer.
 */
export const BrowserLayout = ({
  pane,
  children,
  paneTitle,
  selectionLabel,
  chipIcon = <IconListTree size={14} />,
  selectionKey,
  paneWidth = 260,
  minContentWidth = 480,
  classNames,
}: BrowserLayoutProps) => {
  const {ref, width} = useElementSize();
  // Resolve on the first render: the effect-deferred default renders the narrow chip, then flickers.
  const isWideViewport = useMediaQuery('(min-width: 48em)', true, {
    getInitialValueInEffect: false,
  });
  const isWide =
    isWideViewport &&
    (width === 0 || width >= paneWidth + minContentWidth + 16);
  const [drawerOpened, {open: openDrawer, close: closeDrawer}] =
    useDisclosure(false);

  useEffect(() => {
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  return (
    <Box
      ref={ref}
      className={classNames?.root}
      style={{
        display: 'flex',
        gap: 'var(--mantine-spacing-md)',
        flexDirection: isWide ? 'row' : 'column',
      }}>
      {!isWide && (
        <>
          <UnstyledButton
            onClick={openDrawer}
            aria-label={`Show ${paneTitle}`}
            aria-haspopup="dialog"
            className={classes.chip}>
            <Badge
              component="span"
              size="lg"
              variant="outline"
              color="gray"
              leftSection={chipIcon}
              style={{cursor: 'pointer'}}>
              {selectionLabel}
            </Badge>
          </UnstyledButton>
          <Drawer
            opened={drawerOpened}
            onClose={closeDrawer}
            title={paneTitle}
            position="left"
            closeButtonProps={{'aria-label': `Close ${paneTitle}`}}>
            {pane}
          </Drawer>
        </>
      )}
      {isWide && (
        <Box
          w={paneWidth}
          miw={paneWidth}
          className={`${classes.paneColumn} ${classNames?.pane ?? ''}`}>
          <Box className={classes.paneSticky} data-testid="browser-layout-pane">
            {pane}
          </Box>
        </Box>
      )}
      <Box className={`${classes.contentColumn} ${classNames?.content ?? ''}`}>
        {children}
      </Box>
    </Box>
  );
};
